import { useState } from 'react'

import { useSesion } from '@/modules/auth/useSesion'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Boton } from '@/shared/ui/Boton/Boton'
import { Cargando } from '@/shared/ui/Estados/Cargando'
import { SinDatos } from '@/shared/ui/Estados/SinDatos'
import { Paginacion } from '@/shared/ui/Paginacion/Paginacion'
import { ThOrdenable, type Orden } from '@/shared/ui/Tabla/OrdenTabla'
import { Tarjeta } from '@/shared/ui/Tarjeta/Tarjeta'
import { fecha } from '@/shared/utilidades/formato'

import { DatosDeOperacion } from './DatosDeOperacion'
import { NuevoCliente } from './NuevoCliente'
import { useClientes, type CampoClientes } from './useComercial'
import estilos from './Comercial.module.css'

/**
 * Los clientes de MEGA.
 *
 * Salen de World Office, que es donde vive la contabilidad y por tanto la
 * identidad de los terceros. La matriz solo agrega lo que allá no existe: el
 * WhatsApp al que se manda el acta, la zona, las notas de operación.
 *
 * Son 2.566, así que la lista nunca se trae completa: se busca y se corta.
 */
export function PaginaClientesWorldOffice() {
  const [termino, setTermino] = useState('')
  // Arranca por los que más se cotizan: en una lista de 2.566, los que de
  // verdad se trabajan son un puñado, y son los que se buscan a diario.
  const [orden, setOrden] = useState<Orden<CampoClientes>>({
    campo: 'cotizaciones',
    ascendente: false,
  })
  const [pagina, setPagina] = useState(0)
  const [creando, setCreando] = useState(false)
  /**
   * El cliente al que se le están tocando los datos de operación.
   *
   * Esta pantalla es de solo lectura porque los terceros se crean en World
   * Office, y eso está bien para el NIT y la razón social. Pero de ahí se
   * había saltado a que no se podía tocar NADA, y el WhatsApp al que llega
   * el acta, la dirección de la obra y la zona no son datos contables: son
   * de MEGA, y World Office ni los guarda.
   */
  const [editando, setEditando] = useState<{
    clienteId: string | null
    idTercero: number
    nombre: string
    identificacion: string
  } | null>(null)

  const { puede } = useSesion()
  // Los datos de operación son del maestro de clientes, no de contabilidad.
  const puedeEditar = puede('maestros', 'editar')
  const { filas: clientes, total, cargando, error, porPagina } = useClientes(termino, orden, pagina)

  /**
   * Filtrar vuelve a la primera página, en el mismo gesto.
   *
   * Con un efecto posterior se cuela una consulta que pide la página vieja
   * sobre el resultado nuevo, y el servidor responde 416: rango imposible.
   */
  function filtrar(cambio: () => void) {
    cambio()
    setPagina(0)
  }

  /** Cambiar de columna también vuelve al principio. */
  function ordenar(nuevo: Orden<CampoClientes>) {
    setOrden(nuevo)
    setPagina(0)
  }

  return (
    <Tarjeta
      subtitulo="Vienen de World Office. Los terceros se crean allá, no aquí."
      sinRelleno
      pie={
        <Paginacion pagina={pagina} porPagina={porPagina} total={total} onCambiar={setPagina} />
      }
      acciones={
        <div className={estilos.filtros}>
          <input
            className={estilos.buscador}
            type="search"
            placeholder="Nombre o NIT…"
            value={termino}
            onChange={(e) => filtrar(() => setTermino(e.target.value))}
            aria-label="Buscar cliente"
          />

          {puede('maestros', 'crear') && (
            <Boton variante="principal" onClick={() => setCreando(true)}>
              Nuevo cliente
            </Boton>
          )}
        </div>
      }
    >
      {cargando ? (
        <Cargando mensaje="CONSULTANDO CLIENTES ACTIVOS..." />
      ) : error ? (
        <div style={{ padding: 'var(--e-5)' }}>
          <Aviso tipo="error" titulo="No se pudieron cargar">
            {error}
          </Aviso>
        </div>
      ) : clientes.length === 0 ? (
        <SinDatos
          titulo="Ningún cliente con ese nombre"
          mensaje="Si no aparece, hay que crearlo en World Office."
        />
      ) : (
        <div className={estilos.desplazable}>
          <table className={estilos.tabla}>
            <thead>
              <tr>
                <ThOrdenable campo="identificacion" orden={orden} onCambiar={ordenar} ancho={130}>
                  Documento
                </ThOrdenable>

                <ThOrdenable campo="nombre" orden={orden} onCambiar={ordenar}>
                  Nombre
                </ThOrdenable>

                <th style={{ width: 140 }}>WhatsApp</th>
                <th style={{ width: 130 }}>Zona</th>

                <ThOrdenable
                  campo="ordenes"
                  orden={orden}
                  onCambiar={ordenar}
                  derecha
                  ancho={88}
                  primeroDescendente
                >
                  Órdenes
                </ThOrdenable>

                <ThOrdenable
                  campo="cotizaciones"
                  orden={orden}
                  onCambiar={ordenar}
                  derecha
                  ancho={104}
                  primeroDescendente
                >
                  Cotizaciones
                </ThOrdenable>

                <ThOrdenable
                  campo="ultima_orden"
                  orden={orden}
                  onCambiar={ordenar}
                  ancho={126}
                  primeroDescendente
                >
                  Última orden
                </ThOrdenable>

                {puedeEditar && <th style={{ width: 96 }} />}
              </tr>
            </thead>

            <tbody>
              {clientes.map((cliente) => (
                <tr key={cliente.id_tercero}>
                  <td className={estilos.codigo}>{cliente.identificacion}</td>

                  <td className={estilos.nombre}>
                    <span className={cliente.es_consumidor_final ? estilos.apagado : undefined}>
                      {cliente.nombre}
                    </span>
                    {/* Solo con órdenes vivas. Antes salía con solo estar el
                        cliente registrado, y Consumidor final —con sus dos
                        órdenes anuladas— decía «En la matriz». */}
                    {cliente.ordenes_vivas > 0 && (
                      <span className={estilos.enLaMatriz} style={{ marginLeft: 8 }}>
                        {cliente.ordenes_vivas === 1
                          ? '1 en la matriz'
                          : `${cliente.ordenes_vivas} en la matriz`}
                      </span>
                    )}
                  </td>

                  <td className={cliente.telefono_whatsapp ? estilos.cifra : estilos.tenue}>
                    {cliente.telefono_whatsapp ?? 'Sin registrar'}
                  </td>

                  <td className={cliente.zona_nombre ? undefined : estilos.tenue}>
                    {cliente.zona_nombre ?? '—'}
                  </td>

                  <td className={`${estilos.derecha} ${cliente.ordenes ? estilos.cifra : estilos.tenue}`}>
                    {cliente.ordenes || '—'}
                  </td>

                  <td
                    className={`${estilos.derecha} ${cliente.cotizaciones ? estilos.cifra : estilos.tenue}`}
                  >
                    {cliente.cotizaciones || '—'}
                  </td>

                  <td className={estilos.apagado}>
                    {cliente.ultima_orden ? fecha(cliente.ultima_orden) : '—'}
                  </td>

                  {puedeEditar && (
                    <td className={estilos.derecha}>
                      <Boton
                        variante="plano"
                        tamano="pequeno"
                        onClick={() =>
                          setEditando({
                            clienteId: cliente.cliente_id ?? null,
                            idTercero: cliente.id_tercero,
                            nombre: cliente.nombre ?? 'Sin nombre',
                            identificacion: cliente.identificacion ?? '',
                          })
                        }
                      >
                        {/* «Datos» a secas no se lee como un botón que abre
                            algo, y con el NIT y el nombre bloqueados arriba la
                            página entera parece de solo lectura: Johan la dio
                            por no editable. El verbo va delante. */}
                        ✎ Editar
                      </Boton>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <NuevoCliente abierto={creando} onCerrar={() => setCreando(false)} />

      {editando && (
        <DatosDeOperacion
          abierto
          clienteId={editando.clienteId}
          idTercero={editando.idTercero}
          nombre={editando.nombre}
          identificacion={editando.identificacion}
          onCerrar={() => setEditando(null)}
        />
      )}
    </Tarjeta>
  )
}
